import { useEffect } from 'react';

import { getPublicTrackingConfig } from '@/lib/db/admin/tracking';
import { getConsent, onConsentChanged } from '@/lib/cookie-consent';

// Format guards (defense-in-depth): never inject an id that doesn't match the
// expected shape into a <script> tag, even though the admin field validates too.
const GA_ID_RE = /^(G|UA|AW|GT)-[A-Z0-9-]+$/i;
const META_PIXEL_RE = /^\d{6,20}$/;

// Module-level: a script, once injected, stays for the page lifetime. These flags
// prevent a second injection when consent is re-evaluated. Revoking consent after
// granting it only takes full effect on the next page load (MVP — no Consent Mode).
let gaInjected = false;
let metaInjected = false;

export function useTrackingScripts() {
  useEffect(() => {
    let cancelled = false;

    async function evaluate() {
      const consent = getConsent();
      if (!consent) return; // no decision yet → load nothing
      const needGa = consent.analytics && !gaInjected;
      const needMeta = consent.marketing && !metaInjected;
      if (!needGa && !needMeta) return;

      const config = await getPublicTrackingConfig();
      if (cancelled) return;

      if (needGa && GA_ID_RE.test(config.ga_measurement_id)) {
        gaInjected = true;
        injectGA(config.ga_measurement_id);
      }
      if (needMeta && META_PIXEL_RE.test(config.meta_pixel_id)) {
        metaInjected = true;
        injectMetaPixel(config.meta_pixel_id);
      }
    }

    void evaluate();
    const off = onConsentChanged(() => {
      void evaluate();
    });
    return () => {
      cancelled = true;
      off();
    };
  }, []);
}

function injectGA(measurementId: string) {
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  const inline = document.createElement('script');
  inline.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(inline);
}

function injectMetaPixel(pixelId: string) {
  const inline = document.createElement('script');
  inline.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(inline);
}
